import { forwardRef, useId } from "react";
import { produce } from "immer";
import {
  type PropsWithOptionalTestID,
  CoreCheckboxGroup,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { Fieldset, type FieldsetProps } from "../Fieldset";
import { Checkbox, type CheckboxProps } from "../Checkbox";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

import * as S from "./CheckboxGroup.styled";

type CheckboxGroupInput = Omit<
  CheckboxProps,
  "alertProps" | "id" | "checked" | "disabled" | "onChange" | "children"
> & {
  id: CheckboxProps["id"];
  checked: CheckboxProps["checked"];
  children?: CheckboxGroupInput[];
};

export type CheckboxGroupProps = PropsWithOptionalTestID &
  Pick<
    FieldsetProps,
    "legend" | "legendHint" | "legendTooltipProps" | "legendHintTooltipProps"
  > & {
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    inputs: CheckboxGroupInput[];
    onChange: (inputs: CheckboxGroupInput[]) => void;
  };

const CheckboxGroup = forwardRef<HTMLFieldSetElement, CheckboxGroupProps>(
  (props, ref) => {
    const {
      alertProps,
      legend,
      legendHint,
      legendTooltipProps,
      legendHintTooltipProps,
      inputs,
      onChange,
      testID = "checkbox-group",
    } = props;

    const { tokens } = useDesignTokens();
    const { checkboxGroup } = tokens;

    const alertId = useId();
    const ariaAlertAssociationProps = buildAlertAssociationProps(
      alertId,
      props
    );

    function handleChange(inputProps: CheckboxGroupInput) {
      const updatedInputs = produce(inputs, (draft) => {
        const targetId = inputProps.id;
        const checkedProperty = "checked";

        CoreCheckboxGroup.updateInputs(draft, targetId, checkedProperty);
      });

      onChange(updatedInputs);
    }

    return (
      <InputWrapper
        gap={checkboxGroup.gap}
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <Fieldset
          ref={ref}
          legend={legend}
          legendHint={legendHint}
          legendTooltipProps={legendTooltipProps}
          legendHintTooltipProps={legendHintTooltipProps}
          testID={testID}
          ariaAlertAssociationProps={ariaAlertAssociationProps}
        >
          <S.List>{getComponents(inputs, handleChange)}</S.List>
        </Fieldset>
      </InputWrapper>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

function getComponents(
  inputs: CheckboxGroupInput[],
  handleChange: (inputProps: CheckboxGroupInput) => void
): JSX.Element[] {
  return inputs.map(({ children, ...inputProps }, index) => {
    let ariaControls = undefined;

    if (children && children.length > 0) {
      ariaControls = children.map((item) => item.id).join(" ");
    }

    return (
      <S.ListItem key={index}>
        <Checkbox
          {...inputProps}
          onChange={() => handleChange(inputProps)}
          aria-controls={ariaControls}
        />
        {children && children.length > 0 && (
          <S.NestedList>{getComponents(children, handleChange)}</S.NestedList>
        )}
      </S.ListItem>
    );
  });
}

export default CheckboxGroup;
